/**
 * §B-5 trade-lane editor — Drawer form: name, destination countries (multi),
 * scope, incoterm / IOR type / valuation / duties, optional IOR entity + duties
 * account, required docs. Saves via the ADMIN API with optimistic concurrency.
 */
import { useState } from "react";
import { ApiError, api, type AdminTradeLane, type AdminLegalEntity, type TradeLaneInput } from "../../../lib/api.js";
import { INCOTERMS, DUTIES_PAYORS, IOR_TYPES, VALUE_BASES } from "../../../lib/customsEnums.js";
import { humanizeCode } from "../../../lib/format.js";
import { countryOptions } from "../../../lib/countries.js";
import { Drawer, Field, Input, Select, MultiSelect, Button, useToast } from "../../../components/ui/index.js";

interface StoreOpt {
  id: string;
  label: string;
}

const COUNTRY_OPTS = countryOptions();
const opt = (vals: readonly string[]) => vals.map((v) => ({ value: v, label: humanizeCode(v) }));

export function TradeLaneEditor({
  lane,
  entities,
  stores,
  onClose,
  onSaved,
}: {
  lane: AdminTradeLane | null;
  entities: AdminLegalEntity[];
  stores: StoreOpt[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isNew = lane === null;

  const [name, setName] = useState(lane?.name ?? "");
  const [countries, setCountries] = useState<string[]>(lane?.destinationCountries ?? []);
  const [scope, setScope] = useState(lane?.storeId ?? "");
  const [incoterm, setIncoterm] = useState(lane?.incoterm ?? "DAP");
  const [iorType, setIorType] = useState(lane?.iorType ?? "CUSTOMER");
  const [iorEntityId, setIorEntityId] = useState(lane?.iorEntityId ?? "");
  const [valueBasis, setValueBasis] = useState(lane?.valueBasis ?? "ORDER_PRICE");
  const [dutiesPayor, setDutiesPayor] = useState(lane?.dutiesPayor ?? "RECEIVER");
  const [dutiesAccount, setDutiesAccount] = useState(lane?.dutiesAccount ?? "");
  const [requiredDocs, setRequiredDocs] = useState((lane?.requiredDocs ?? []).join(", "));
  const [enabled, setEnabled] = useState(lane?.enabled ?? true);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) return toast("Name is required.", "danger");
    if (countries.length === 0) return toast("Pick at least one destination country.", "danger");
    if (iorType === "ENTITY" && !iorEntityId) return toast("Choose the importer entity.", "danger");
    const input: TradeLaneInput = {
      name: name.trim(),
      destinationCountries: countries,
      storeId: scope || null,
      incoterm,
      iorType,
      iorEntityId: iorType === "ENTITY" ? iorEntityId || null : null,
      valueBasis,
      dutiesPayor,
      dutiesAccount: dutiesAccount.trim() || null,
      requiredDocs: requiredDocs.split(",").map((s) => s.trim()).filter(Boolean),
      enabled,
    };
    setSaving(true);
    try {
      if (isNew) await api.admin.createTradeLane(input);
      else await api.admin.updateTradeLane(lane.id, input, lane.updatedAt);
      toast(isNew ? "Lane created" : "Lane saved", "success");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("This lane changed since you opened it — reloading.", "danger");
        if (!isNew) onSaved();
      } else if (e instanceof ApiError && e.status === 400) {
        toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid lane.", "danger");
      } else if (e instanceof ApiError && e.status === 403) {
        toast("Admin role required.", "danger");
      } else {
        toast("Couldn’t save the lane.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer onClose={onClose} ariaLabel={isNew ? "New trade lane" : `Edit ${lane.name}`} title={isNew ? "New trade lane" : "Edit trade lane"} meta={<span className="muted">More specific lanes (shorter country list, store-scoped) win.</span>}>
      <div className="rule-form">
        <div className="rule-form__row2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="EU → US export" autoFocus />
          </Field>
          <Field label="Scope">
            <Select ariaLabel="Scope" value={scope} onChange={setScope} options={[{ value: "", label: "All stores" }, ...stores.map((s) => ({ value: s.id, label: s.label }))]} />
          </Field>
        </div>

        <Field label="Destination countries">
          <MultiSelect searchable ariaLabel="Destination countries" placeholder="Pick countries" summaryNoun="countries" searchPlaceholder="Country" value={countries} onChange={setCountries} options={COUNTRY_OPTS} />
        </Field>

        <div className="rule-form__row2">
          <Field label="Incoterm">
            <Select ariaLabel="Incoterm" value={incoterm} onChange={setIncoterm} options={opt(INCOTERMS)} />
          </Field>
          <Field label="Valuation basis">
            <Select ariaLabel="Valuation basis" value={valueBasis} onChange={setValueBasis} options={opt(VALUE_BASES)} />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Importer of record">
            <Select ariaLabel="IOR type" value={iorType} onChange={setIorType} options={opt(IOR_TYPES)} />
          </Field>
          <Field label="IOR entity">
            <Select
              ariaLabel="IOR entity"
              value={iorEntityId}
              onChange={setIorEntityId}
              options={[{ value: "", label: iorType === "ENTITY" ? "Choose…" : "n/a (customer)" }, ...entities.map((e) => ({ value: e.id, label: e.name }))]}
            />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Duties payor">
            <Select ariaLabel="Duties payor" value={dutiesPayor} onChange={setDutiesPayor} options={opt(DUTIES_PAYORS)} />
          </Field>
          <Field label="Duties account (optional)">
            <Input value={dutiesAccount} onChange={(e) => setDutiesAccount(e.target.value)} placeholder="carrier duties account" />
          </Field>
        </div>

        <div className="rule-form__row2">
          <Field label="Required docs (comma-separated)">
            <Input value={requiredDocs} onChange={(e) => setRequiredDocs(e.target.value)} placeholder="commercial-invoice, packing-list" />
          </Field>
          <Field label="Status">
            <label className="rule-form__toggle">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>{enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </Field>
        </div>

        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create lane" : "Save changes"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
